/**
 * =============================================================================
 * ROASTBREW - THEME CONTEXT
 * =============================================================================
 *
 * Single source of truth for theme. Derives the active color tokens and the
 * light/dark flag from the user's tier in ONE place, so no component has to
 * recompute `currentTier === 'free'` or look up TIER_COLORS itself.
 *
 * USAGE (route-level pages + shared layout only):
 *   import { useTheme } from './ThemeContext';
 *   const { colors, isLight } = useTheme();
 *
 * Sub-components inside a page do NOT call this — the page passes `colors`
 * down to them as a prop. Context at the boundary, props within a feature.
 *
 * PROVIDED VALUES:
 * - colors:  the TIER_COLORS palette for the active tier
 * - isLight: true on the free (light) tier, false on pro / pro_plus
 *
 * =============================================================================
 */

import React, { createContext, useContext, useMemo } from 'react';
import { TIER_COLORS, isLightTheme } from './config';

const ThemeContext = createContext(null);

export function ThemeProvider({ tier = 'free', children }) {
  const value = useMemo(() => ({
    colors: TIER_COLORS[tier] || TIER_COLORS.free,
    isLight: isLightTheme(tier),
  }), [tier]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return ctx;
}

export default ThemeContext;
